import { useSyncExternalStore } from 'react';

/**
 * useMediaQuery — subscribe to a CSS media query and re-render on change.
 *
 * SSR/first-paint safe via useSyncExternalStore: the server snapshot returns
 * `false` so markup is deterministic, then the client hydrates to the real
 * match. Prefer Tailwind responsive classes for pure styling; reach for this
 * only when a component must make a *structural* JS decision (e.g. render a
 * full-screen sheet vs. an inline popover, or mount a different subtree).
 *
 *   const isMobile = useMediaQuery('(max-width: 767px)');
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = (onChange: () => void) => {
    if (typeof window === 'undefined' || !window.matchMedia) return () => {};
    const mql = window.matchMedia(query);
    // addEventListener is supported in all evergreen browsers; older Safari
    // used addListener — guard for safety.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  };

  const getSnapshot = () => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  };

  const getServerSnapshot = () => false;

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Tailwind's default breakpoints, mirrored here so JS decisions stay in lockstep
 * with the CSS `sm:`/`md:`/`lg:`/`xl:`/`2xl:` prefixes. See design-system README
 * for the responsive policy (base < 640 = phone; md = tablet; lg = desktop).
 */
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/** True when the viewport is at least the given breakpoint (min-width). */
export function useBreakpoint(bp: Breakpoint): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS[bp]}px)`);
}

/**
 * Convenience: true below the `md` breakpoint (< 768px) — the "phone" band that
 * the mobile-first work targets. Equivalent to `!useBreakpoint('md')`.
 */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.md - 1}px)`);
}
